import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getQuotes, createQuote, deleteQuote, getClientsDropdown, getPropertyAddresses, getAllUsersDropdown } from '../services/api';
import { formatDate, getPerthDateOffset } from '../utils/dateUtils';
import { Plus, Search, FileText, X, Loader2, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { DatePicker } from '../components/DateTimePicker';
import { SearchableSelect } from '../components/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { canSeePrices } from '../utils/permissions';

const statusTabs = [
  { value: 99, label: 'All' },
  { value: 0, label: 'Drafts' },
  { value: 1, label: 'Assigned' },
  { value: 2, label: 'For Submission' },
  { value: 3, label: 'For Acceptance' },
  { value: 4, label: 'Accepted' },
  { value: 5, label: 'Job Booked' },
  { value: 6, label: 'Invoiced' },
  { value: -1, label: 'Canceled' },
  { value: -2, label: 'Declined' },
];

const statusLabels = {
  '-2': { label: 'Declined', color: 'bg-red-100 text-red-800' },
  '-1': { label: 'Canceled', color: 'bg-slate-200 text-slate-700' },
  '0': { label: 'Draft', color: 'bg-slate-200 text-slate-700' },
  '1': { label: 'Assigned', color: 'bg-yellow-100 text-yellow-800' },
  '2': { label: 'For Submission', color: 'bg-blue-100 text-blue-800' },
  '3': { label: 'For Acceptance', color: 'bg-cyan-100 text-cyan-800' },
  '4': { label: 'Accepted', color: 'bg-green-100 text-green-800' },
  '5': { label: 'Job Booked', color: 'bg-purple-100 text-purple-800' },
  '6': { label: 'Invoiced', color: 'bg-emerald-100 text-emerald-800' },
};

// Create Quote Modal
function QuoteModal({ isOpen, onClose, onSubmit, loading }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    client_id: '',
    property_address_id: '',
    job_title: '',
    description: '',
    expiry_date: getPerthDateOffset(30),
    attachments: [],
  });
  const fileInputRef = useRef(null);

  const { data: clients } = useQuery({
    queryKey: ['clientsDropdown'],
    queryFn: () => getClientsDropdown().then(res => res.data),
    enabled: isOpen,
  });

  const { data: addresses } = useQuery({
    queryKey: ['propertyAddresses', formData.client_id],
    queryFn: () => getPropertyAddresses({ client_id: formData.client_id }).then(res => res.data),
    enabled: !!formData.client_id,
  });

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, { name: file.name, data: e.target.result, type: file.type }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!formData.property_address_id) {
      toast.error('Please select a property address');
      return;
    }
    onSubmit(formData);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, { name: file.name, data: e.target.result, type: file.type }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">Create Quote</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Client Dropdown (searchable) */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Client</label>
            <SearchableSelect
              value={formData.client_id}
              onChange={(v) => setFormData({ ...formData, client_id: v, property_address_id: '' })}
              options={(clients || []).map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Select Client"
              testId="quote-client-select"
            />
          </div>

          {/* Property Address Dropdown (searchable) */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Property Address</label>
            <SearchableSelect
              value={formData.property_address_id}
              onChange={(v) => setFormData({ ...formData, property_address_id: v })}
              options={(addresses || []).map((a) => ({
                value: a.id,
                label: [a.address_line1, a.city, a.region, a.postal_code].filter(Boolean).join(', '),
              }))}
              placeholder="Select Property Address"
              disabled={!formData.client_id}
              testId="quote-property-select"
            />
          </div>

          {/* Job Title & Expiry Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Job Title</label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., ROOF WORKS, PAINTING"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Expiry Date</label>
              <DatePicker
                value={formData.expiry_date}
                onChange={(v) => setFormData({ ...formData, expiry_date: v })}
              />
            </div>
          </div>

          {/* Rich Text Description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Description</label>
            <RichTextEditor 
              value={formData.description} 
              onChange={(val) => setFormData({ ...formData, description: val })}
            />
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Attachments</label>
            <div 
              className="border-2 border-dashed border-slate-300 rounded p-6 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <Upload size={24} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-500">Drop your attachments here.</p>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                multiple 
                onChange={handleFileChange}
              />
            </div>
            {formData.attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {formData.attachments.map((file, idx) => (
                  <div key={`att-${file.name}-${idx}`} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                    <span className="text-sm truncate">{file.name}</span>
                    <button type="button" onClick={() => removeAttachment(idx)} className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 text-slate-600 rounded hover:bg-slate-50">Cancel</button>
            <button 
              type="submit" 
              disabled={loading} 
              className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-2 disabled:opacity-50" 
              data-testid="save-quote-btn"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Quotes() {
  const { user: currentUser } = useAuth();
  const showPrices = canSeePrices(currentUser);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(99);
  const [showModal, setShowModal] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: staffUsers } = useQuery({
    queryKey: ['usersDropdownAll'],
    queryFn: () => getAllUsersDropdown().then(res => res.data),
  });

  const { data: quotes, isLoading } = useQuery({
    queryKey: ['quotes', activeTab, createdByFilter],
    queryFn: () => getQuotes({ status: activeTab === 99 ? undefined : activeTab, user_filter: createdByFilter || undefined }).then(res => res.data),
  });

  const createMutation = useMutation({
    mutationFn: createQuote,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      setShowModal(false);
      toast.success('Quote created successfully');
      // Redirect to Quote Detail page
      if (res.data?.quote_id) {
        navigate(`/quotes/${res.data.quote_id}`);
      } else if (res.data?.id) {
        navigate(`/quotes/${res.data.id}`);
      }
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to create quote'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteQuote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Quote removed');
    },
  });

  // Filter and sort quotes
  const filteredQuotes = quotes?.filter(q => 
    q.quote_number?.toLowerCase().includes(search.toLowerCase()) ||
    q.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    q.property_address?.toLowerCase().includes(search.toLowerCase()) ||
    q.job_titles?.toLowerCase().includes(search.toLowerCase())
  );

  // Sort
  const sortedQuotes = filteredQuotes?.sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  // Paginate
  const totalPages = Math.ceil((sortedQuotes?.length || 0) / entriesPerPage);
  const paginatedQuotes = sortedQuotes?.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);

  // formatDate imported from utils/dateUtils

  const formatCurrency = (num) => {
    if (!num) return '0.00';
    return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => (
    <span className="inline-flex flex-col ml-1 text-slate-400">
      <ChevronUp size={12} className={sortField === field && sortDir === 'asc' ? 'text-primary' : ''} />
      <ChevronDown size={12} className={sortField === field && sortDir === 'desc' ? 'text-primary' : ''} style={{marginTop: '-4px'}} />
    </span>
  );

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="quotes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Quotes</h1>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        {/* Status Tabs & Create Button */}
        <div className="p-4 border-b flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-1">
            {statusTabs.map(tab => (
              <button
                key={tab.value}
                onClick={() => { setActiveTab(tab.value); setCurrentPage(1); }}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  activeTab === tab.value 
                    ? 'bg-primary text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button 
            onClick={() => setShowModal(true)} 
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded hover:bg-primary-hover"
            data-testid="add-quote-btn"
          >
            <Plus size={16} />
            Create Quote
          </button>
        </div>

        {/* Table Controls */}
        <div className="p-4 flex flex-col sm:flex-row justify-between gap-4 border-b">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select 
                value={entriesPerPage} 
                onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-slate-300 rounded"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 whitespace-nowrap">User:</span>
              <select
                value={createdByFilter}
                onChange={(e) => { setCreatedByFilter(e.target.value); setCurrentPage(1); }}
                className="px-2 py-1 border border-slate-300 rounded text-sm min-w-[140px]"
                data-testid="quotes-created-by-filter"
              >
                <option value="">All Users</option>
                {staffUsers?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Search:</span>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap cursor-pointer" onClick={() => handleSort('created_at')}>
                  Date <SortIcon field="created_at" />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap cursor-pointer" onClick={() => handleSort('quote_number')}>
                  Quote No. <SortIcon field="quote_number" />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap cursor-pointer" onClick={() => handleSort('client_name')}>
                  Client <SortIcon field="client_name" />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Address</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Job Title</th>
                {showPrices && (
                  <th className="px-4 py-3 text-center font-semibold text-slate-600 whitespace-nowrap cursor-pointer" onClick={() => handleSort('total_cost')}>
                    Total Cost <SortIcon field="total_cost" />
                  </th>
                )}
                <th className="px-4 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Job(s)</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Created By</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Status</th>
                <th className="px-4 py-3 bg-white"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={showPrices ? 10 : 9} className="px-4 py-12 text-center">
                    <Loader2 size={24} className="animate-spin mx-auto text-primary" />
                  </td>
                </tr>
              ) : paginatedQuotes?.length === 0 ? (
                <tr>
                  <td colSpan={showPrices ? 10 : 9} className="px-4 py-12 text-center">
                    <FileText size={48} className="mx-auto text-slate-300" />
                    <p className="mt-2 text-slate-500">No Results Found</p>
                  </td>
                </tr>
              ) : (
                paginatedQuotes?.map((quote) => (
                  <tr key={quote.id} className="hover:bg-slate-50" id={`quote-${quote.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(quote.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/quotes/${quote.id}`} className="text-primary hover:underline font-medium">
                        {quote.quote_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{quote.client_name || '-'}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{quote.property_address ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(quote.property_address)}`} target="_blank" rel="noopener noreferrer" className="text-[#3498db] hover:underline" data-testid="address-map-link">{quote.property_address}</a> : '-'}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{quote.job_titles || '-'}</td>
                    {showPrices && <td className="px-4 py-3 text-right font-medium">{formatCurrency(quote.total_cost)}</td>}
                    <td className="px-4 py-3 text-center">{quote.jobs_created || '0 / 0'}</td>
                    <td className="px-4 py-3 text-slate-600 text-sm" data-testid={`quote-created-by-${quote.id}`}>{quote.added_by_name || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${statusLabels[quote.status]?.color || 'bg-slate-100'}`}>
                        {statusLabels[quote.status]?.label || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to remove Quote ${quote.quote_number}?`)) {
                              deleteMutation.mutate(quote.id);
                            }
                          }} 
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Remove
                        </button>
                        <Link 
                          to={`/quotes/${quote.id}`} 
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Details
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-slate-600">
            Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, sortedQuotes?.length || 0)} of {sortedQuotes?.length || 0} entries
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
              if (page > totalPages) return null;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 text-sm border rounded ${currentPage === page ? 'bg-primary text-white' : 'hover:bg-slate-50'}`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2">...</span>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-3 py-1 text-sm border rounded hover:bg-slate-50"
                >
                  {totalPages}
                </button>
              </>
            )}
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1 text-sm border rounded hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <QuoteModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        onSubmit={(data) => createMutation.mutate(data)} 
        loading={createMutation.isPending} 
      />
    </div>
  );
}

export default Quotes;
